import React from 'react';
import { makeStyles, Popover, Typography } from '@material-ui/core';
import ArrowDropDownOutlinedIcon from '@material-ui/icons/ArrowDropDownOutlined';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import classnames from 'classnames';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import { Filter, FunctionField } from 'react-admin';

import ActionButton from 'components/buttons/ActionButton';
import { Actions, Datagrid, List } from 'components/list';
import { ActionField, DateField } from 'components/fields';

import NameField from '../NameField';
import EditableNumberField from './EditableNumberField';
import DeleteLevelActionField from './DeleteLevelActionField';
import EditLevelActionField from './EditLevelActionField';
import AddLevelButton from './AddLevelButton';

const LevelActions = (props) => {
  return (
    <Actions {...props}>
      <AddLevelButton />
    </Actions>
  );
};

// Uses CSS counters since list context hooks aren't available in this RA version.
// Assumes each data row maps to one <tr> in the datagrid body.
const useStyles = makeStyles((theme) => ({
  levelsTable: {
    '& .datagrid-body': {
      counterReset: 'level-row',
    },
    '& .datagrid-body tr': {
      counterIncrement: 'level-row',
    },
  },
  rowNumber: {
    '&::before': {
      content: 'counter(level-row)',
    },
  },
  form: {
    width: '100%',
    maxWidth: theme.spacing(40),
    '& .filter-field': {
      width: '100%',
    },
  },
  datePickerButton: {
    marginTop: theme.spacing(2),
    minWidth: 220,
    '& .MuiButton-label': {
      justifyContent: 'space-between',
      width: '100%',
    },
  },
  datePickerPopover: {
    '& .MuiToolbar-root': {
      display: 'none',
    },
  },
  dateButtonError: {
    color: theme.palette.error.main,
  },
  datePickerInput: {
    marginTop: theme.spacing(2),
    '& .MuiPickersDay-daySelected': {
      color: theme.palette.common.white,
    },
  },
}));

const VALID_AT_DATE_FORMAT = 'YYYY-MM-DD';
const VALID_AT_DATE_TIME_FORMAT = 'YYYY-MM-DD[T]23:59:00.000[Z]';

const parseValidAt = (value) => {
  if (!value) {
    return undefined;
  }

  const parsedValue = moment(value, VALID_AT_DATE_FORMAT, true);
  if (!parsedValue.isValid()) {
    return undefined;
  }

  return parsedValue.format(VALID_AT_DATE_TIME_FORMAT);
};

const formatValidAt = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const parsedValue = moment(value);
  if (!parsedValue.isValid()) {
    return '';
  }

  return parsedValue.utc().format(VALID_AT_DATE_FORMAT);
};

const parseDateTime = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const parsedValue = moment(value);
  if (!parsedValue.isValid()) {
    return null;
  }

  return parsedValue;
};

const getLevelStatus = (validFrom, validUntil) => {
  const now = moment();
  const validFromDate = parseDateTime(validFrom);
  const validUntilDate = parseDateTime(validUntil);

  if (!validFromDate) {
    return '-';
  }

  if (validUntil == null) {
    if (now.isBefore(validFromDate)) {
      return 'Scheduled (Future)';
    }

    if (validFromDate.isSameOrBefore(now)) {
      return 'Live (Current / Ongoing)';
    }
  }

  if (validUntilDate) {
    if (now.isAfter(validUntilDate)) {
      return 'Ended (Closed)';
    }

    if (validFromDate.isSameOrBefore(now) && now.isSameOrBefore(validUntilDate)) {
      return 'Closing (Ends in the future)';
    }
  }

  return '-';
};

const ValidAtFilterInput = ({ label, input: { value, onChange }, meta: { touched, error } }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateChange = (newValue) => {
    if (!newValue) {
      onChange(undefined);
      handleClose();
      return;
    }

    const formattedDate = parseValidAt(moment(newValue).format(VALID_AT_DATE_FORMAT));
    onChange(formattedDate);
    handleClose();
  };

  const formattedValue = formatValidAt(value);
  const selectedDate = formattedValue ? moment(formattedValue, VALID_AT_DATE_FORMAT) : null;
  const displayValue = selectedDate?.isValid() ? selectedDate.format('LL') : '';

  return (
    <>
      <Popover
        className={classes.datePickerPopover}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <DatePicker
            className={classes.datePickerInput}
            autoOk
            clearable
            views={['month', 'date']}
            value={selectedDate}
            onChange={handleDateChange}
            open={Boolean(anchorEl)}
            variant="static"
          />
        </MuiPickersUtilsProvider>
      </Popover>

      <ActionButton
        onClick={handleOpen}
        className={classnames(
          classes.datePickerButton,
          touched && !!error && classes.dateButtonError
        )}
        endIcon={<ArrowDropDownOutlinedIcon />}>
        {`${label}: ${displayValue || '--'}`}
      </ActionButton>
    </>
  );
};

const LevelsFilter = (props) => {
  const classes = useStyles();

  return (
    <Filter classes={classes} {...props}>
      <ValidAtFilterInput source="validAt" label="Valid at" alwaysOn />
    </Filter>
  );
};

const RowNumberField = ({ className, rowNumberClassName }) => (
  <Typography
    component="span"
    variant="body2"
    className={classnames(className, rowNumberClassName)}
  />
);

const Levels = (props) => {
  const classes = useStyles();

  return (
    <List
      {...props}
      resource="rewardsLevel"
      basePath="/rewardsSettings"
      pagination={false}
      filters={<LevelsFilter />}
      actions={<LevelActions />}>
      <Datagrid className={classes.levelsTable}>
        <RowNumberField
          source="rowNumber"
          label="No."
          sortable={false}
          rowNumberClassName={classes.rowNumber}
        />
        <NameField source="name" imageSource="iconUrl" sortable={false} />
        <EditableNumberField source="minPoints" sortable={false} />
        <EditableNumberField source="minShipments" sortable={false} />
        <DateField source="validFrom" label="Valid from" sortable={false} withTime />
        <DateField source="validUntil" label="Valid until" sortable={false} withTime />
        <FunctionField
          label="Status"
          sortable={false}
          render={(record) => getLevelStatus(record?.validFrom, record?.validUntil)}
        />
        <ActionField>
          <EditLevelActionField />
          <DeleteLevelActionField />
        </ActionField>
      </Datagrid>
    </List>
  );
};

export default Levels;
